import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { formatRestTime } from '@heraion/shared';

import { webTapFloor } from '../theme';
import type { useTheme } from './theme';

// JS-only replacement for the native @react-native-community/slider inside the
// timer pickers. The native Slider crashes on mount inside a RN <Modal> under
// the New Architecture (Fabric) — the Modal is a separate Fabric surface and
// the native slider view dies attaching to it (RN 0.85.3, slider 5.2.0, no
// upstream fix). A pure-RN control (Views/Text/Pressable) has no native
// component, so it mounts fine in the Modal. It also matches the logger's own
// stepper idiom (SetRowStepperBar) and restores the pre-slider preset picker:
// −/+ for fine tuning, preset chips for coarse jumps. Reused by the logger gear
// picker + both Settings timer sheets.

type RestStepperProps = {
  value: number;
  min: number;
  max: number;
  /** Fine ± increment (seconds). */
  step: number;
  onChange: (seconds: number) => void;
  disabled?: boolean;
  /** Quick-jump presets (seconds), clamped to [min,max] on render. */
  presets?: number[];
  /** Readout/preset formatter. Defaults to formatRestTime (mm:ss) so the
   *  three timer callsites are unchanged; the clean-reps prompt passes a
   *  bare-count formatter (AP-0p) to reuse the same stepper for reps. */
  formatValue?: (n: number) => string;
  /** Screen-reader labels for the − / + buttons. Default to the rest-timer's
   *  "seconds" wording; a non-seconds reuse (e.g. the clean-reps prompt) MUST
   *  override both so the reader doesn't announce "1 seconds" on a rep count. */
  stepDownLabel?: string;
  stepUpLabel?: string;
  theme: ReturnType<typeof useTheme>;
};

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

export function RestStepper({
  value,
  min,
  max,
  step,
  onChange,
  disabled = false,
  presets = [],
  formatValue,
  stepDownLabel,
  stepUpLabel,
  theme,
}: RestStepperProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const fmt = formatValue ?? formatRestTime;
  const canDec = !disabled && value > min;
  const canInc = !disabled && value < max;

  return (
    <View style={styles.wrap}>
      <View style={styles.stepRow}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            stepDownLabel ??
            t('mobile.logger.restStepMinus', {
              defaultValue: 'Decrease by {{step}} seconds',
              step,
            })
          }
          accessibilityState={{ disabled: !canDec }}
          disabled={!canDec}
          hitSlop={8}
          onPress={() => onChange(clamp(value - step, min, max))}
          style={({ pressed }) => [
            styles.stepButton,
            !canDec && styles.stepButtonDisabled,
            pressed && canDec && styles.pressed,
          ]}
        >
          <Text style={styles.stepGlyph}>−</Text>
        </Pressable>

        <Text style={styles.readout} accessibilityLabel={fmt(value)}>
          {fmt(value)}
        </Text>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            stepUpLabel ??
            t('mobile.logger.restStepPlus', {
              defaultValue: 'Increase by {{step}} seconds',
              step,
            })
          }
          accessibilityState={{ disabled: !canInc }}
          disabled={!canInc}
          hitSlop={8}
          onPress={() => onChange(clamp(value + step, min, max))}
          style={({ pressed }) => [
            styles.stepButton,
            !canInc && styles.stepButtonDisabled,
            pressed && canInc && styles.pressed,
          ]}
        >
          <Text style={styles.stepGlyph}>+</Text>
        </Pressable>
      </View>

      {presets.length > 0 ? (
        <View style={styles.presetRow}>
          {presets
            .map((p) => clamp(p, min, max))
            .filter((p, i, a) => a.indexOf(p) === i)
            .map((p) => {
              const active = p === value;
              return (
                <Pressable
                  key={p}
                  accessibilityRole="button"
                  accessibilityLabel={fmt(p)}
                  accessibilityState={{ selected: active, disabled }}
                  disabled={disabled}
                  hitSlop={{ top: 8, bottom: 8 }}
                  onPress={() => onChange(p)}
                  style={({ pressed }) => [
                    styles.presetChip,
                    active && styles.presetChipActive,
                    pressed && styles.pressed,
                  ]}
                >
                  <Text
                    style={[
                      styles.presetText,
                      active && styles.presetTextActive,
                    ]}
                  >
                    {fmt(p)}
                  </Text>
                </Pressable>
              );
            })}
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s3 },
    stepRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
    },
    stepButton: {
      minWidth: 44,
      minHeight: webTapFloor ?? 36,
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    stepButtonDisabled: { opacity: 0.4 },
    stepGlyph: {
      color: p.ink,
      fontSize: t.sizes.xl,
      fontWeight: '700',
      lineHeight: t.sizes.xl + 4,
    },
    readout: {
      flex: 1,
      textAlign: 'center',
      color: p.ink,
      fontSize: t.sizes['2xl'],
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
    presetRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: s.s2,
    },
    presetChip: {
      minHeight: webTapFloor ?? 36,
      paddingHorizontal: s.s3,
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Selected preset: brand-tinted fill + brand border (not colour-only —
    // carries accessibilityState.selected + a heavier weight too).
    presetChipActive: {
      backgroundColor: p.brand100,
      borderColor: p.brand500,
    },
    presetText: {
      color: p.ink2,
      fontSize: t.sizes.base,
      fontWeight: '600',
      fontVariant: ['tabular-nums'],
    },
    presetTextActive: {
      color: p.ink,
      fontWeight: '700',
    },
    pressed: { opacity: 0.7 },
  });
}
